import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChartLine, FaArrowUp, FaArrowDown, FaExchangeAlt, FaShieldAlt, FaBitcoin, FaEthereum } from 'react-icons/fa';
import { io } from 'socket.io-client';
import { usePrices } from '../../../context/PriceContext';

const extractPrice = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val);
    if (Array.isArray(val)) return parseFloat(val[5] || val[0] || 0); 
    if (typeof val === 'object' && val !== null) {
        return parseFloat(val.lp || val.price || val.last_price || val.mark_price || val.data || 0);
    }
    return 0;
};

const PriceFlash = ({ value, prefix = "$" }) => {
    const numericValue = extractPrice(value);
    const [prevValue, setPrevValue] = useState(numericValue);
    const [flash, setFlash] = useState('');

    useEffect(() => {
        if (numericValue > prevValue) setFlash('flash-green');
        else if (numericValue < prevValue) setFlash('flash-red');
        
        if (numericValue !== prevValue) {
            const timer = setTimeout(() => setFlash(''), 1000);
            setPrevValue(numericValue);
            return () => clearTimeout(timer);
        }
    }, [numericValue]);

    return (
        <span className={`price-text ${flash}`}>
            {prefix}{numericValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    );
};

const CoinDCXDashboard = () => {
    const [asset, setAsset] = useState('BTC');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const prices = usePrices();
    const livePriceFromAppSocket = asset === 'BTC' ? prices['BTCUSD'] : prices['ETHUSD'];

    const TRADE_URL = window.location.hostname === "localhost" 
        ? "http://localhost:8000" 
        : `${window.location.protocol}//${window.location.hostname}:8000`;

    useEffect(() => {
        const tradeSocket = io(TRADE_URL);
        
        tradeSocket.on('connect', () => {
            tradeSocket.emit('get_coindcx_data', asset);
        });

        tradeSocket.on('coindcx_update', (update) => {
            if (update.asset === asset) {
                setData(update);
                setLoading(false);
                setError(null);
            }
        });

        tradeSocket.on('connect_error', () => {
            setError('Waiting for connection to trade server (Port 8000)...');
        });

        return () => tradeSocket.disconnect();
    }, [asset]);

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    return (
        <motion.div 
            className="container-fluid py-4"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            style={{ 
                background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)',
                minHeight: '100vh',
                color: '#f8fafc',
                fontFamily: "'Inter', sans-serif"
            }}
        >
            {/* Header Section */}
            <div className="row mb-4">
                <div className="col-12 d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="fw-bold mb-1 d-flex align-items-center" style={{ letterSpacing: '-0.5px' }}>
                            <img src="https://coindcx.com/assets/favicon.png" alt="CoinDCX" className="me-2" style={{ width: '32px', height: '32px' }} />
                            CoinDCX Pro Terminal
                        </h2>
                        <p className="text-white-50 small mb-0">Real-time market monitoring & automated futures execution</p>
                    </div>
                    <div className="btn-group p-1" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                        <button 
                            className={`btn btn-sm px-4 ${asset === 'BTC' ? 'btn-primary' : 'text-white'}`}
                            onClick={() => setAsset('BTC')}
                            style={{ borderRadius: '10px' }}
                        >
                            BTC
                        </button>
                        <button 
                            className={`btn btn-sm px-4 ${asset === 'ETH' ? 'btn-primary' : 'text-white'}`}
                            onClick={() => setAsset('ETH')}
                            style={{ borderRadius: '10px' }}
                        >
                            ETH
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger border-0 shadow-sm mb-4" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#fca5a5' }}>
                    {error}
                </div>
            )}

            {/* Main Stats */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-lg" style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)', borderRadius: '24px' }}>
                        <div className="card-body p-4">
                            <h6 className="text-white-50 small text-uppercase fw-bold mb-3">Last Price ({asset}/USDT)</h6>
                            <h2 className="mb-0 fw-bold text-white display-6">
                                <PriceFlash value={data?.last_price || livePriceFromAppSocket || 0} />
                                <span className={`ms-3 small ${data?.stats?.change >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '18px' }}>
                                    {data?.stats?.change >= 0 ? <FaArrowUp /> : <FaArrowDown />} 
                                    {Math.abs(data?.stats?.change || 0).toFixed(2)}%
                                </span>
                            </h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-8">
                    <div className="card h-100 border-0 shadow-lg" style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)', borderRadius: '24px' }}>
                        <div className="card-body p-4">
                            <div className="row">
                                <div className="col-4 border-end border-secondary">
                                    <h6 className="text-white-50 small text-uppercase fw-bold mb-2">24h High</h6>
                                    <h4 className="mb-0 text-white">${data?.stats?.high?.toLocaleString()}</h4>
                                </div>
                                <div className="col-4 border-end border-secondary ps-4">
                                    <h6 className="text-white-50 small text-uppercase fw-bold mb-2">24h Low</h6>
                                    <h4 className="mb-0 text-white">${data?.stats?.low?.toLocaleString()}</h4>
                                </div>
                                <div className="col-4 ps-4">
                                    <h6 className="text-white-50 small text-uppercase fw-bold mb-2">24h Volume</h6>
                                    <h4 className="mb-0 text-white">{data?.stats?.volume?.toLocaleString()} {asset}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Trading Actions */}
            <div className="row g-4">
                <div className="col-md-6">
                    <div className="card border-0 overflow-hidden h-100" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2) !important', borderRadius: '24px' }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="mb-0 fw-bold text-success">Quick Long</h4>
                                <FaChartLine className="text-success fs-3" />
                            </div>
                            <p className="text-white-50 small mb-4">Execute a market buy order for {asset} with automated TP/SL based on strategy.</p>
                            <button className="btn btn-success w-100 py-3 fw-bold shadow-lg" style={{ borderRadius: '14px', letterSpacing: '1px' }}>
                                PLACE BUY ORDER
                            </button>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card border-0 overflow-hidden h-100" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2) !important', borderRadius: '24px' }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="mb-0 fw-bold text-danger">Quick Short</h4>
                                <FaChartLine className="text-danger fs-3" style={{ transform: 'scaleY(-1)' }} />
                            </div>
                            <p className="text-white-50 small mb-4">Execute a market sell order for {asset} with automated TP/SL based on strategy.</p>
                            <button className="btn btn-danger w-100 py-3 fw-bold shadow-lg" style={{ borderRadius: '14px', letterSpacing: '1px' }}>
                                PLACE SELL ORDER
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .price-text { transition: color 0.3s ease; }
                .flash-green { color: #4ade80 !important; text-shadow: 0 0 10px rgba(74, 222, 128, 0.5); }
                .flash-red { color: #f87171 !important; text-shadow: 0 0 10px rgba(248, 113, 113, 0.5); }
                .btn-success { background: #10b981; border: none; }
                .btn-danger { background: #ef4444; border: none; }
            `}</style>
        </motion.div>
    );
};

export default CoinDCXDashboard;
